interface ResourceManifestEntry {
  id: string;
  kind: 'model-thumbnail' | 'fabric-style-thumbnail' | 'fabric-style-preview';
  url: string;
  revision: string;
  prefetch: boolean;
}

interface ResourceManifest {
  schemaVersion: 1;
  resources: ResourceManifestEntry[];
}

const RESOURCE_KINDS = new Set<ResourceManifestEntry['kind']>([
  'model-thumbnail',
  'fabric-style-thumbnail',
  'fabric-style-preview',
]);

export interface ResolvedPersistentResource {
  url: string;
  objectUrl: boolean;
}

interface ActivationFlight {
  accountId: string;
  promise: Promise<void>;
}

interface ResourceSnapshot {
  activation: number;
  accountId: string;
  cacheName: string;
  identity: string;
  currentUrl: string;
  fallbackUrls: string[];
  signal: AbortSignal;
}

export interface ResourceManifestDiffItem {
  id: string;
  kind: ResourceManifestEntry['kind'];
  identity: string;
  url: string;
}

export interface ResourceManifestDiff {
  added: ResourceManifestDiffItem[];
  changed: ResourceManifestDiffItem[];
  revoked: ResourceManifestDiffItem[];
}

export function subscribeResourceManifestDiff(
  kinds: ResourceManifestDiffItem['kind'][],
  listener: (diff: ResourceManifestDiff) => void,
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const allowedKinds = new Set(kinds);
  const handler = (event: Event) => {
    const diff = (event as CustomEvent<ResourceManifestDiff>).detail;
    if (!diff) return;
    const entries = [...diff.added, ...diff.changed, ...diff.revoked];
    if (entries.some((entry) => allowedKinds.has(entry.kind))) listener(diff);
  };
  target.addEventListener(MANIFEST_DIFF_EVENT, handler);
  return () => target.removeEventListener(MANIFEST_DIFF_EVENT, handler);
}

const CACHE_NAMESPACE_PREFIX = 'afs-ui-resources-';
const CACHE_PREFIX = `${CACHE_NAMESPACE_PREFIX}v1:`;
const UPDATE_EVENT = 'cloud-resource-cache-update';
const ACCESS_CHANGE_EVENT = 'cloud-resource-access-change';
const MANIFEST_DIFF_EVENT = 'cloud-resource-manifest-diff';
const ACTIVATION_THROTTLE_MS = 10_000;
const MAX_RESOURCE_DOWNLOADS = 4;
const RESOURCE_FETCH_TIMEOUT_MS = 30_000;
const MAX_MANIFEST_RESOURCES = 5_000;

let activeAccountId: string | null = null;
let activeCacheName: string | null = null;
let activationId = 0;
let allowedResources = new Map<string, ResourceManifestEntry>();
let cachedRequests = new Map<string, string[]>();
let activationFlight: ActivationFlight | null = null;
let lastActivationAccountId: string | null = null;
let lastActivationStartedAt = 0;
let pendingNamespacePurgeAccountId: string | null = null;
let cacheNamespaceQueue: Promise<void> = Promise.resolve();
let activeDownloadController = new AbortController();
const updateJobs = new Map<string, Promise<void>>();

class AsyncLimiter {
  private active = 0;
  private readonly waiters: Array<() => void> = [];

  constructor(private readonly limit: number) {}

  async run<T>(work: () => Promise<T>): Promise<T> {
    if (this.active >= this.limit) {
      await new Promise<void>((resolve) => this.waiters.push(resolve));
    } else {
      this.active++;
    }
    try {
      return await work();
    } finally {
      const next = this.waiters.shift();
      if (next) next();
      else this.active--;
    }
  }
}

const resourceDownloadLimiter = new AsyncLimiter(MAX_RESOURCE_DOWNLOADS);

function withCacheNamespaceLock<T>(work: () => Promise<T>): Promise<T> {
  const result = cacheNamespaceQueue.then(work, work);
  cacheNamespaceQueue = result.then(() => undefined, () => undefined);
  return result;
}

function canUsePersistentCache(): boolean {
  try {
    return Boolean(window.CLOUD_RUNTIME && window.isSecureContext && 'caches' in window);
  } catch {
    return false;
  }
}

function cacheName(accountId: string): string {
  return `${CACHE_PREFIX}${accountId}`;
}

function absoluteUrl(url: string): string {
  return new URL(url, window.location.origin).href;
}

function expectedPath(kind: ResourceManifestEntry['kind']): RegExp {
  return {
    'model-thumbnail': /^\/api\/files\/models\/[0-9a-f-]+\/preview$/i,
    'fabric-style-thumbnail': /^\/api\/files\/fabric-display-styles\/[a-z0-9-]+\/thumbnail$/i,
    'fabric-style-preview': /^\/api\/files\/fabric-display-styles\/[a-z0-9-]+\/preview$/i,
  }[kind];
}

function hasOneLegalRevision(url: URL, expectedRevision?: string): boolean {
  const keys = [...url.searchParams.keys()];
  const revisions = url.searchParams.getAll('v');
  return keys.length === 1
    && keys[0] === 'v'
    && revisions.length === 1
    && /^[a-z0-9_-]{1,128}$/i.test(revisions[0] || '')
    && (expectedRevision == null || revisions[0] === expectedRevision);
}

function parseResourceManifest(value: unknown): ResourceManifest {
  if (!value || typeof value !== 'object') throw new Error('Invalid resource cache manifest');
  const candidate = value as Partial<ResourceManifest>;
  if (candidate.schemaVersion !== 1 || !Array.isArray(candidate.resources)) {
    throw new Error('Unsupported resource cache manifest');
  }
  if (candidate.resources.length > MAX_MANIFEST_RESOURCES) throw new Error('Resource cache manifest is too large');
  const identities = new Set<string>();
  const resources = candidate.resources.map((rawEntry) => {
    if (!rawEntry || typeof rawEntry !== 'object') throw new Error('Invalid resource manifest entry');
    const entry = rawEntry as Partial<ResourceManifestEntry>;
    if (
      typeof entry.id !== 'string'
      || !RESOURCE_KINDS.has(entry.kind as ResourceManifestEntry['kind'])
      || typeof entry.url !== 'string'
      || typeof entry.revision !== 'string'
      || typeof entry.prefetch !== 'boolean'
    ) {
      throw new Error('Invalid resource manifest entry');
    }
    const parsedUrl = new URL(entry.url, window.location.origin);
    if (
      parsedUrl.origin !== window.location.origin
      || !expectedPath(entry.kind as ResourceManifestEntry['kind']).test(parsedUrl.pathname)
      || parsedUrl.hash
      || !hasOneLegalRevision(parsedUrl, entry.revision)
    ) {
      throw new Error('Resource manifest URL is not an allowed versioned asset');
    }
    if (identities.has(parsedUrl.pathname)) throw new Error('Duplicate resource manifest identity');
    identities.add(parsedUrl.pathname);
    return entry as ResourceManifestEntry;
  });
  return { schemaVersion: 1, resources };
}

export function resourceIdentity(url: string): string {
  return new URL(url, window.location.origin).pathname;
}

function emitResourceUpdates(identities: Iterable<string>): void {
  for (const identity of identities) {
    try {
      window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { identity } }));
    } catch {
      // Cache invalidation remains fail-closed even if an event shim is broken.
    }
  }
}

function emitModelAccessChanges(identities: Iterable<string>): void {
  const modelIds = [...identities].flatMap((identity) => {
    const match = identity.match(/^\/api\/files\/models\/([0-9a-f-]+)\/preview$/i);
    return match?.[1] ? [match[1]] : [];
  });
  if (!modelIds.length) return;
  try {
    window.dispatchEvent(new CustomEvent(ACCESS_CHANGE_EVENT, { detail: { modelIds } }));
  } catch {
    // State invalidation remains best effort after the cache is already closed.
  }
}

function manifestDiffItem(entry: ResourceManifestEntry): ResourceManifestDiffItem {
  return { id: entry.id, kind: entry.kind, identity: resourceIdentity(entry.url), url: entry.url };
}

function emitManifestDiff(diff: ResourceManifestDiff): void {
  if (!diff.added.length && !diff.changed.length && !diff.revoked.length) return;
  try {
    window.dispatchEvent(new CustomEvent(MANIFEST_DIFF_EVENT, { detail: diff }));
  } catch {
    // Resource bytes remain isolated even if consumers cannot refresh their catalogs.
  }
}

function rotateDownloadGeneration(): void {
  activeDownloadController.abort();
  activeDownloadController = new AbortController();
}

function failClosed(accountId: string | null, expectedActivation?: number): void {
  if (expectedActivation != null && expectedActivation !== activationId) return;
  rotateDownloadGeneration();
  const previousIdentities = [...allowedResources.keys()];
  activeAccountId = accountId;
  activeCacheName = null;
  allowedResources = new Map();
  cachedRequests = new Map();
  updateJobs.clear();
  emitResourceUpdates(previousIdentities);
}

export function prepareResourceCacheAccount(accountId: string, options: { force?: boolean } = {}): void {
  if (activeAccountId === accountId && !options.force) return;
  const switchingAccount = activeAccountId !== accountId;
  ++activationId;
  activationFlight = null;
  if (switchingAccount) pendingNamespacePurgeAccountId = accountId;
  failClosed(accountId);
}

function isActivationCurrent(accountId: string, expectedActivation: number): boolean {
  return activeAccountId === accountId && activationId === expectedActivation;
}

function schedulePrefetch(entries: ResourceManifestEntry[]): void {
  if (!entries.length) return;
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  };
  if (idleWindow.requestIdleCallback) {
    idleWindow.requestIdleCallback(() => void prefetchResources(entries), { timeout: 1_500 });
  } else {
    window.setTimeout(() => void prefetchResources(entries), 250);
  }
}

async function purgeNamespaceBeforeManifest(
  accountId: string,
  expectedActivation: number,
  nextCacheName: string,
  purgeAll: boolean,
): Promise<void> {
  await withCacheNamespaceLock(async () => {
    if (!isActivationCurrent(accountId, expectedActivation)) return;
    const names = await caches.keys();
    if (!isActivationCurrent(accountId, expectedActivation)) return;
    for (const name of names) {
      if (!name.startsWith(CACHE_NAMESPACE_PREFIX) || (!purgeAll && name === nextCacheName)) continue;
      if (!isActivationCurrent(accountId, expectedActivation)) return;
      await caches.delete(name);
    }
  });
}

function cachedUrlForAllowedIdentity(
  requestUrl: string,
  nextAllowed: Map<string, ResourceManifestEntry>,
): { identity: string; url: string } | null {
  let parsed: URL;
  try {
    parsed = new URL(requestUrl, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin || parsed.hash || !hasOneLegalRevision(parsed)) return null;
  const allowed = nextAllowed.get(parsed.pathname);
  if (!allowed || !expectedPath(allowed.kind).test(parsed.pathname)) return null;
  return { identity: parsed.pathname, url: parsed.href };
}

function isImageResponse(response: Response | undefined): response is Response {
  return Boolean(response?.headers.get('Content-Type')?.toLowerCase().startsWith('image/'));
}

async function indexAndPruneCache(
  cache: Cache,
  nextAllowed: Map<string, ResourceManifestEntry>,
): Promise<Map<string, string[]>> {
  const requests = await cache.keys();
  const nextIndex = new Map<string, string[]>();
  for (const request of requests) {
    const candidate = cachedUrlForAllowedIdentity(request.url, nextAllowed);
    const response = candidate ? await cache.match(request) : undefined;
    if (!candidate || !isImageResponse(response)) {
      await cache.delete(request);
      continue;
    }
    nextIndex.set(candidate.identity, [...(nextIndex.get(candidate.identity) || []), candidate.url]);
  }
  for (const [identity, urls] of nextIndex) {
    const currentUrl = absoluteUrl(nextAllowed.get(identity)!.url);
    if (!urls.includes(currentUrl)) continue;
    await Promise.all(urls.filter((url) => url !== currentUrl).map((url) => cache.delete(url)));
    nextIndex.set(identity, [currentUrl]);
  }
  return nextIndex;
}

async function fetchResourceManifest(accountId: string, expectedActivation: number): Promise<ResourceManifest> {
  const response = await fetch('/api/library/resource-manifest', {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (response.status === 401 && isActivationCurrent(accountId, expectedActivation)) {
    window.dispatchEvent(new CustomEvent('cloud-auth-expired'));
  }
  if (!response.ok) throw new Error(`Resource manifest failed (${response.status})`);
  return parseResourceManifest(await response.json());
}

async function performActivation(accountId: string): Promise<void> {
  rotateDownloadGeneration();
  const currentActivation = ++activationId;
  if (!canUsePersistentCache()) {
    failClosed(accountId, currentActivation);
    return;
  }

  const nextCacheName = cacheName(accountId);
  const purgeAll = pendingNamespacePurgeAccountId === accountId;
  const previousAllowed = new Map(allowedResources);
  try {
    await purgeNamespaceBeforeManifest(accountId, currentActivation, nextCacheName, purgeAll);
  } catch (error) {
    failClosed(accountId, currentActivation);
    console.warn('Resource cache namespace cleanup failed:', error);
    return;
  }
  if (!isActivationCurrent(accountId, currentActivation)) return;
  if (purgeAll && pendingNamespacePurgeAccountId === accountId) pendingNamespacePurgeAccountId = null;

  let manifest: ResourceManifest;
  try {
    manifest = await fetchResourceManifest(accountId, currentActivation);
  } catch (error) {
    failClosed(accountId, currentActivation);
    console.warn('Resource cache manifest refresh failed:', error);
    return;
  }
  if (!isActivationCurrent(accountId, currentActivation)) return;

  const nextAllowed = new Map(manifest.resources.map((entry) => [resourceIdentity(entry.url), entry]));
  let nextIndex: Map<string, string[]> | null = null;
  try {
    nextIndex = await withCacheNamespaceLock(async () => {
      if (!isActivationCurrent(accountId, currentActivation)) return null;
      const cache = await caches.open(nextCacheName);
      if (!isActivationCurrent(accountId, currentActivation)) return null;
      const index = await indexAndPruneCache(cache, nextAllowed);
      return isActivationCurrent(accountId, currentActivation) ? index : null;
    });
  } catch (error) {
    failClosed(accountId, currentActivation);
    console.warn('Resource cache reconciliation failed:', error);
    return;
  }
  if (!nextIndex || !isActivationCurrent(accountId, currentActivation)) return;

  const diff: ResourceManifestDiff = { added: [], changed: [], revoked: [] };
  const changedIdentities = new Set<string>();
  for (const [identity, entry] of nextAllowed) {
    const previous = previousAllowed.get(identity);
    if (!previous) {
      diff.added.push(manifestDiffItem(entry));
      changedIdentities.add(identity);
    } else if (absoluteUrl(previous.url) !== absoluteUrl(entry.url)) {
      diff.changed.push(manifestDiffItem(entry));
      changedIdentities.add(identity);
    }
  }
  const revokedIdentities = [...previousAllowed.keys()].filter((identity) => !nextAllowed.has(identity));
  revokedIdentities.forEach((identity) => {
    changedIdentities.add(identity);
    diff.revoked.push(manifestDiffItem(previousAllowed.get(identity)!));
  });
  activeCacheName = nextCacheName;
  allowedResources = nextAllowed;
  cachedRequests = nextIndex;
  emitManifestDiff(diff);
  emitResourceUpdates(changedIdentities);
  emitModelAccessChanges(revokedIdentities);
  schedulePrefetch(manifest.resources.filter((entry) => entry.prefetch));
}

export function activateResourceCache(accountId: string, options: { force?: boolean } = {}): Promise<void> {
  prepareResourceCacheAccount(accountId);
  if (activationFlight?.accountId === accountId) return activationFlight.promise;
  const now = Date.now();
  if (!options.force && lastActivationAccountId === accountId && now - lastActivationStartedAt < ACTIVATION_THROTTLE_MS) {
    return Promise.resolve();
  }
  lastActivationAccountId = accountId;
  lastActivationStartedAt = now;
  const promise = performActivation(accountId).finally(() => {
    if (activationFlight?.promise === promise) activationFlight = null;
  });
  activationFlight = { accountId, promise };
  return promise;
}

function clearMemoryState(): void {
  ++activationId;
  rotateDownloadGeneration();
  const identities = [...allowedResources.keys()];
  activeAccountId = null;
  activeCacheName = null;
  allowedResources = new Map();
  cachedRequests = new Map();
  activationFlight = null;
  lastActivationAccountId = null;
  lastActivationStartedAt = 0;
  pendingNamespacePurgeAccountId = null;
  updateJobs.clear();
  emitResourceUpdates(identities);
}

export async function clearResourceCache(accountId?: string | null): Promise<void> {
  const target = accountId || activeAccountId;
  clearMemoryState();
  if (!target || !('caches' in window)) return;
  try {
    await withCacheNamespaceLock(() => caches.delete(cacheName(target)).then(() => undefined));
  } catch (error) {
    console.warn('Resource cache cleanup failed:', error);
  }
}

export async function clearAllResourceCaches(): Promise<void> {
  clearMemoryState();
  if (!('caches' in window)) return;
  try {
    await withCacheNamespaceLock(async () => {
      const names = await caches.keys();
      await Promise.all(names
        .filter((name) => name.startsWith(CACHE_NAMESPACE_PREFIX))
        .map((name) => caches.delete(name)));
    });
  } catch (error) {
    console.warn('Resource cache cleanup failed:', error);
  }
}

async function prefetchResources(entries: ResourceManifestEntry[]): Promise<void> {
  // One background request at a time leaves the shared limiter immediately
  // available for visible images and avoids queueing the entire catalog.
  for (const entry of entries) await updateResource(entry.url).catch(() => undefined);
}

function captureResourceSnapshot(url: string): ResourceSnapshot | null {
  const identity = resourceIdentity(url);
  const allowed = allowedResources.get(identity);
  if (!activeAccountId || !activeCacheName || !allowed) return null;
  return {
    activation: activationId,
    accountId: activeAccountId,
    cacheName: activeCacheName,
    identity,
    currentUrl: absoluteUrl(allowed.url),
    fallbackUrls: [...(cachedRequests.get(identity) || [])],
    signal: activeDownloadController.signal,
  };
}

function isSnapshotCurrent(snapshot: ResourceSnapshot): boolean {
  const allowed = allowedResources.get(snapshot.identity);
  return activationId === snapshot.activation
    && activeDownloadController.signal === snapshot.signal
    && !snapshot.signal.aborted
    && activeAccountId === snapshot.accountId
    && activeCacheName === snapshot.cacheName
    && Boolean(allowed)
    && absoluteUrl(allowed!.url) === snapshot.currentUrl;
}

async function fetchResourceImage(snapshot: ResourceSnapshot): Promise<Response> {
  const requestController = new AbortController();
  const abortRequest = () => requestController.abort();
  if (snapshot.signal.aborted) abortRequest();
  else snapshot.signal.addEventListener('abort', abortRequest, { once: true });
  const timeout = window.setTimeout(abortRequest, RESOURCE_FETCH_TIMEOUT_MS);
  let rejectOnAbort: (() => void) | undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectOnAbort = () => reject(new DOMException('Resource download aborted', 'AbortError'));
    if (requestController.signal.aborted) rejectOnAbort();
    else requestController.signal.addEventListener('abort', rejectOnAbort, { once: true });
  });
  try {
    return await Promise.race([
      fetch(snapshot.currentUrl, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'image/avif,image/webp,image/png,image/jpeg,image/*' },
        signal: requestController.signal,
      }),
      aborted,
    ]);
  } finally {
    window.clearTimeout(timeout);
    snapshot.signal.removeEventListener('abort', abortRequest);
    if (rejectOnAbort) requestController.signal.removeEventListener('abort', rejectOnAbort);
  }
}

function isSnapshotCacheUrl(snapshot: ResourceSnapshot, url: string): boolean {
  const candidate = cachedUrlForAllowedIdentity(url, allowedResources);
  return Boolean(candidate && candidate.identity === snapshot.identity);
}

async function readCachedResponse(snapshot: ResourceSnapshot, url: string): Promise<Response | undefined> {
  return withCacheNamespaceLock(async () => {
    if (!isSnapshotCurrent(snapshot) || !isSnapshotCacheUrl(snapshot, url)) return undefined;
    const cache = await caches.open(snapshot.cacheName);
    if (!isSnapshotCurrent(snapshot)) return undefined;
    const response = await cache.match(url);
    if (!isSnapshotCurrent(snapshot)) return undefined;
    if (response && !isImageResponse(response)) {
      await cache.delete(url);
      return undefined;
    }
    if (response && url === snapshot.currentUrl) {
      const staleUrls = snapshot.fallbackUrls.filter((candidate) => candidate !== snapshot.currentUrl);
      await Promise.all(staleUrls.map((candidate) => cache.delete(candidate)));
    }
    return response;
  });
}

async function updateResource(url: string): Promise<void> {
  const snapshot = captureResourceSnapshot(url);
  if (!snapshot || snapshot.currentUrl !== absoluteUrl(url)) return;
  const jobKey = `${snapshot.activation}:${snapshot.accountId}:${snapshot.currentUrl}`;
  const existing = updateJobs.get(jobKey);
  if (existing) return existing;

  const job = (async () => {
    const exact = await readCachedResponse(snapshot, snapshot.currentUrl);
    if (exact) {
      if (isSnapshotCurrent(snapshot)) {
        const nextIndex = new Map(cachedRequests);
        nextIndex.set(snapshot.identity, [snapshot.currentUrl]);
        cachedRequests = nextIndex;
      }
      return;
    }
    let response: Response | null;
    try {
      response = await resourceDownloadLimiter.run(async () => {
        if (!isSnapshotCurrent(snapshot) || snapshot.signal.aborted) return null;
        const fetched = await fetchResourceImage(snapshot);
        if (!fetched.ok || !isImageResponse(fetched)) throw new Error(`Resource update failed (${fetched.status})`);
        return fetched;
      });
    } catch (error) {
      if (snapshot.signal.aborted || (error as DOMException)?.name === 'AbortError') return;
      throw error;
    }
    if (!response || !isSnapshotCurrent(snapshot)) return;

    const committed = await withCacheNamespaceLock(async () => {
      if (!isSnapshotCurrent(snapshot)) return false;
      const targetCache = await caches.open(snapshot.cacheName);
      if (!isSnapshotCurrent(snapshot)) return false;
      await targetCache.put(snapshot.currentUrl, response.clone());
      if (!isSnapshotCurrent(snapshot)) {
        await targetCache.delete(snapshot.currentUrl).catch(() => false);
        return false;
      }
      const staleUrls = snapshot.fallbackUrls.filter((candidate) => candidate !== snapshot.currentUrl);
      await Promise.all(staleUrls.map((staleUrl) => targetCache.delete(staleUrl)));
      return isSnapshotCurrent(snapshot);
    });
    if (!committed || !isSnapshotCurrent(snapshot)) return;
    const nextIndex = new Map(cachedRequests);
    nextIndex.set(snapshot.identity, [snapshot.currentUrl]);
    cachedRequests = nextIndex;
    emitResourceUpdates([snapshot.identity]);
  })();
  updateJobs.set(jobKey, job);
  try {
    await job;
  } finally {
    if (updateJobs.get(jobKey) === job) updateJobs.delete(jobKey);
  }
}

function networkFallback(url: string, snapshot?: ResourceSnapshot | null): ResolvedPersistentResource {
  return {
    url: snapshot && isSnapshotCurrent(snapshot) ? snapshot.currentUrl : url,
    objectUrl: false,
  };
}

export async function resolvePersistentResource(url: string): Promise<ResolvedPersistentResource> {
  let snapshot: ResourceSnapshot | null = null;
  try {
    if (!canUsePersistentCache()) return networkFallback(url);
    snapshot = captureResourceSnapshot(url);
    if (!snapshot) return networkFallback(url);

    let response = await readCachedResponse(snapshot, snapshot.currentUrl);
    let usedFallback = false;
    if (!response) {
      for (const fallbackUrl of snapshot.fallbackUrls) {
        if (fallbackUrl === snapshot.currentUrl) continue;
        response = await readCachedResponse(snapshot, fallbackUrl);
        if (response) {
          usedFallback = true;
          break;
        }
      }
    }
    if (!response) {
      await updateResource(snapshot.currentUrl);
      if (!isSnapshotCurrent(snapshot)) return networkFallback(url);
      response = await readCachedResponse(snapshot, snapshot.currentUrl);
    } else if (usedFallback) {
      void updateResource(snapshot.currentUrl).catch(() => undefined);
    }
    if (!response) return networkFallback(url, snapshot);

    const blob = await response.blob();
    if (!isSnapshotCurrent(snapshot)) return networkFallback(url);
    const objectUrl = URL.createObjectURL(blob);
    if (!isSnapshotCurrent(snapshot)) {
      try { URL.revokeObjectURL(objectUrl); } catch { /* best effort */ }
      return networkFallback(url);
    }
    return { url: objectUrl, objectUrl: true };
  } catch (error) {
    console.warn('Persistent resource read failed:', error);
    return networkFallback(url, snapshot);
  }
}

export function subscribeResourceUpdates(url: string, listener: () => void): () => void {
  const identity = resourceIdentity(url);
  const handler = (event: Event) => {
    if ((event as CustomEvent<{ identity?: string }>).detail?.identity === identity) listener();
  };
  window.addEventListener(UPDATE_EVENT, handler);
  return () => window.removeEventListener(UPDATE_EVENT, handler);
}
